import { Alert, Anchor, Button, Stack, Text, Textarea } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconBan, IconLock } from '@tabler/icons-react'
import { type DocStatus, docStatusOf, formatDateTime, type Locale, labels, type RefMap, type Resource } from '@tnn/core'
import { ResponsiveDialog, StatusPill, useLinkComponent } from '@tnn/ui'
import type { TFunction } from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isApiError } from '../api/client'
import type { Rec } from '../api/resources'

/** Draft, submitted or cancelled, next to a document's title */
export function DocStatusPill({ resource, record }: { resource: Resource; record?: Rec }) {
  const { t } = useTranslation()
  const status = record ? docStatusOf(record) : 'draft'
  const field = resource.field('docstatus')
  return <StatusPill size="sm" tone={field?.tones?.[status] ?? 'neutral'} label={t(labels.option(resource, 'docstatus', status))} />
}

/** Resolves true when the person confirms, false when they close or cancel; `danger` for what cannot be taken back */
export function confirmAsync(opts: { title: string; body: string; confirm: string; cancel: string; danger?: boolean }): Promise<boolean> {
  return new Promise((resolve) =>
    modals.openConfirmModal({
      title: opts.title,
      children: <Text size="sm">{opts.body}</Text>,
      labels: { confirm: opts.confirm, cancel: opts.cancel },
      confirmProps: opts.danger ? { color: 'danger' } : undefined,
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false),
      onClose: () => resolve(false),
    }),
  )
}

/**
 * Above a document's sections: what its state means for the reader and where
 * it came from. Drafts say nothing unless they amend a cancelled document.
 */
export function DocBanner({
  resource,
  path,
  record,
  refs,
  editableFields,
  editing,
  locale,
}: {
  resource: Resource
  path: string
  record: Rec
  refs: RefMap
  /** Labels of the fields that can change after submit, when the person may change them */
  editableFields: string[]
  /** The form is in "edit after submit" mode */
  editing: boolean
  locale: Locale
}) {
  const { t } = useTranslation()
  const Link = useLinkComponent()
  const status: DocStatus = docStatusOf(record)
  const at = (value: unknown) => (value ? formatDateTime(String(value), { locale }) : '–')
  const by = (value: unknown) => String(value ?? '–')
  const source = record.amendedFrom ? String(record.amendedFrom) : undefined
  const amended = source && (
    <Text size="sm">
      {t('doc.banner.amendedFrom')}{' '}
      <Anchor component={Link} href={`/${path}/${source}`} size="sm">
        {refs[resource.key]?.[source] ?? source}
      </Anchor>
    </Text>
  )

  if (status === 'cancelled') {
    return (
      <Alert variant="light" color="danger" radius="lg" icon={<IconBan size={20} stroke={1.75} />}>
        <Stack gap={2}>
          <Text size="sm">{t('doc.banner.cancelled', { at: at(record.cancelledAt), by: by(record.cancelledBy) })}</Text>
          {record.cancelReason ? <Text size="sm">{t('doc.banner.reason', { reason: String(record.cancelReason) })}</Text> : null}
          {amended}
        </Stack>
      </Alert>
    )
  }
  if (status === 'submitted') {
    return (
      <Alert variant="light" color="gray" radius="lg" icon={<IconLock size={20} stroke={1.75} />}>
        <Stack gap={2}>
          <Text size="sm">
            {editing
              ? t('doc.banner.editingAfterSubmit', { fields: editableFields.join(', ') })
              : t(editableFields.length ? 'doc.banner.submittedEditable' : 'doc.banner.submitted', {
                  at: at(record.submittedAt),
                  by: by(record.submittedBy),
                })}
          </Text>
          {amended}
        </Stack>
      </Alert>
    )
  }
  return amended ? (
    <Alert variant="light" color="gray" radius="lg">
      {amended}
    </Alert>
  ) : null
}

/** Asks for the reason (required for tax documents and receipts) before cancelling. */
export function CancelDialog({
  title,
  required,
  onClose,
  onCancel,
  t,
}: {
  title: string
  required: boolean
  onClose: () => void
  /** Cancels the document; rejects with the API error */
  onCancel: (reason: string | undefined) => Promise<void>
  t: TFunction
}) {
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)
  const confirm = async () => {
    if (required && !reason.trim()) return setError(t('doc.confirm.reasonRequired'))
    setBusy(true)
    try {
      await onCancel(reason.trim() || undefined)
      onClose()
    } catch (err) {
      setError(isApiError(err) ? (err.issues?.[0]?.message ?? err.message) : t('errors.generic'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <ResponsiveDialog
      opened
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <>
          <Button variant="default" onClick={onClose}>
            {t('actions.back')}
          </Button>
          <Button color="danger" loading={busy} onClick={confirm}>
            {t('doc.actions.cancel')}
          </Button>
        </>
      }
    >
      <Stack gap="md">
        <Text size="sm">{t('doc.confirm.cancelBody')}</Text>
        <Textarea
          data-autofocus
          label={t('doc.confirm.reason')}
          withAsterisk={required}
          autosize
          minRows={2}
          maxLength={500}
          value={reason}
          error={error}
          onChange={(e) => {
            setReason(e.currentTarget.value)
            setError(undefined)
          }}
        />
      </Stack>
    </ResponsiveDialog>
  )
}
